import React from 'react';
import KpiCard from '../../components/KpiCard.js';
import { SECTION_STATUS } from './useDashboardData.js';
import { DASHBOARD_MESSAGES, NOT_APPLICABLE } from './dashboardConstants.js';
import {
    CHANGE_DIRECTIONS,
    CHANGE_KINDS,
    formatChange,
    formatCount,
    formatRate,
} from './dashboardFormat.js';

const KPI_CARD_COUNT = 4;

/**
 * KPI カード 4 枚（SCR-P-001 DP-P-001〜004。API `GET /api/dashboard/kpi`。集計 API 設計 §4.1）
 *
 * 決定率・平均発話比率が出せない（null）ときは「N/A」と出し、0% と出さない（§5.2 #2・#1958 の決定 D3）。
 * 期間の範囲を指定しているときは、値は範囲の合計で、前期比は「前期比 —」にする
 * （期間どうしの比較〈12/22〉を作るまで。§4.2。PM の決定 2026-10-29、#661 comment 6485292094 の 3 の a）。
 * 決定率は常に「前期比 —」にする（change.decision_rate は応答にあるが使わない。§6.5 の 36。#2178）。
 *
 * @param {object} kpi - 集計 API 設計 §4.1 の応答
 * @param {{hasRange?: boolean}} [options]
 */
export function buildKpiCards(kpi, { hasRange = false } = {}) {
    const { current } = kpi;
    // 応答の change に値が入っていても出さない（比べる前の期間が範囲に対して決まっていないため）
    const change = hasRange ? {} : kpi.change;
    const speechRatio = current.avg_agent_speech_ratio;
    const diarizedCount = current.diarized_meeting_count;

    return [
        {
            key: 'meeting',
            title: '面談件数',
            value: formatCount(current.meeting_count),
            valueSuffix: '件',
            change: formatChange(change.meeting_count, CHANGE_KINDS.COUNT, CHANGE_DIRECTIONS.HIGHER_IS_BETTER),
        },
        {
            key: 'recommendation',
            title: '推薦件数',
            value: formatCount(current.recommendation_count),
            valueSuffix: '件',
            change: formatChange(change.recommendation_count, CHANGE_KINDS.COUNT, CHANGE_DIRECTIONS.HIGHER_IS_BETTER),
        },
        {
            key: 'decisionRate',
            title: '決定率',
            value: formatRate(current.decision_rate),
            // 数え終わるまでの時間が短い今の期間ほど低く出るため、差が成績と関係なく下向きに偏る
            change: formatChange(null, CHANGE_KINDS.RATE, CHANGE_DIRECTIONS.NONE),
            note: current.decision_rate === null ? DASHBOARD_MESSAGES.noMeetings : undefined,
        },
        {
            key: 'speechRatio',
            title: '平均発話比率',
            valuePrefix: speechRatio === null ? undefined : 'エージェント',
            value: speechRatio === null ? NOT_APPLICABLE : formatRate(speechRatio),
            // 平均発話比率は良し悪しの色を付けない（C10）
            change: formatChange(change.avg_agent_speech_ratio, CHANGE_KINDS.RATE, CHANGE_DIRECTIONS.NONE),
            note: `対象の面談 ${formatCount(diarizedCount)} 件`,
        },
    ];
}

function KpiCards({ section, onReload, hasRange = false }) {
    if (section.status === SECTION_STATUS.LOADING) {
        return (
            <div className="dashboard-kpi-grid" role="status" aria-label="読み込み中">
                {Array.from({ length: KPI_CARD_COUNT }).map((_, i) => (
                    <div className="kpi-card skeleton-fade-in" key={i}>
                        <div className="skeleton-line dashboard-kpi-skeleton-title" />
                        <div className="skeleton-line dashboard-kpi-skeleton-value" />
                    </div>
                ))}
            </div>
        );
    }
    if (section.status === SECTION_STATUS.FORBIDDEN) {
        return <p className="dashboard-section-message" role="alert">{DASHBOARD_MESSAGES.forbidden}</p>;
    }
    if (section.status === SECTION_STATUS.ERROR) {
        return (
            <div className="dashboard-section-message" role="alert">
                <p>{DASHBOARD_MESSAGES.failed}</p>
                <button type="button" className="chart-card-reload" onClick={onReload}>
                    {DASHBOARD_MESSAGES.reload}
                </button>
            </div>
        );
    }

    return (
        <div className="dashboard-kpi-grid">
            {buildKpiCards(section.data, { hasRange }).map(({ key, ...card }) => <KpiCard key={key} {...card} />)}
        </div>
    );
}

export default KpiCards;
